// The light and dark themes: data-theme on <html>, cached in cs-theme for the next first paint.
import type { ThemePreference } from "@cosmosai/shared";
import { useSyncExternalStore } from "react";
import { announcePreference } from "../preference-events";

export type Theme = "light" | "dark";
export type { ThemePreference };

/** A per-viewer cache read by public/theme-boot.js; the saved preference wins over it (1d). */
export const THEME_KEY = "cs-theme";

const darkScheme = () => window.matchMedia?.("(prefers-color-scheme: dark)");

let preference: ThemePreference | null = null;
let stopFollowingSystem: (() => void) | null = null;

export const currentTheme = (): Theme =>
  document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";

/** The chosen theme: light, dark, or system (the cache, else what the boot script resolved). */
export function currentThemePreference(): ThemePreference {
  if (preference) return preference;
  try {
    const cached = localStorage.getItem(THEME_KEY);
    if (cached === "light" || cached === "dark" || cached === "system") return cached;
  } catch {
    // Storage blocked: the boot script followed the system.
  }
  return "system";
}

const resolve = (choice: ThemePreference): Theme =>
  choice === "system" ? (darkScheme()?.matches ? "dark" : "light") : choice;

/** Shows a theme and caches it for the next first paint; "system" follows the OS setting live. */
export function applyTheme(choice: ThemePreference): void {
  preference = choice;
  document.documentElement.setAttribute("data-theme", resolve(choice));
  stopFollowingSystem?.();
  stopFollowingSystem = null;
  const scheme = darkScheme();
  if (choice === "system" && scheme) {
    const follow = () => document.documentElement.setAttribute("data-theme", resolve("system"));
    scheme.addEventListener("change", follow);
    stopFollowingSystem = () => scheme.removeEventListener("change", follow);
  }
  try {
    localStorage.setItem(THEME_KEY, choice);
  } catch {
    // Storage blocked: the theme lasts for this page only.
  }
}

/** The user's choice: applied at once and announced so it is saved. */
export function chooseTheme(choice: ThemePreference): void {
  applyTheme(choice);
  announcePreference({ theme: choice });
}

export const toggleTheme = (): void => chooseTheme(currentTheme() === "dark" ? "light" : "dark");

/** Subscribes to an attribute of an element, for useSyncExternalStore. */
export function watchAttribute(element: () => Element, name: string) {
  return (onChange: () => void) => {
    const observer = new MutationObserver(onChange);
    observer.observe(element(), { attributes: true, attributeFilter: [name] });
    return () => observer.disconnect();
  };
}

const subscribe = watchAttribute(() => document.documentElement, "data-theme");

/** The current theme, re-rendering when it changes. */
export const useTheme = (): Theme => useSyncExternalStore(subscribe, currentTheme);
